import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import moment from 'moment';

type BackOfficeOrdersFilterContextValue = {
  startISO: string;
  endISO: string;
  /**
   * When false, unpaid lists must omit bp_start_date/bp_end_date so the backend
   * falls back to epoch → now. Used by the "Still Owed" deep link, whose figure
   * is an all-time snapshot rather than a period total.
   */
  dateFilterActive: boolean;
  setDateFilterActive: (active: boolean) => void;
  setRange: (startISO: string, endISO: string) => void;
  visible: boolean;
  setVisible: (visible: boolean) => void;
};

const BackOfficeOrdersFilterContext = createContext<
  BackOfficeOrdersFilterContextValue | undefined
>(undefined);

export const useBackOfficeOrdersFilter =
  (): BackOfficeOrdersFilterContextValue => {
    const ctx = useContext(BackOfficeOrdersFilterContext);
    if (!ctx) {
      throw new Error(
        'useBackOfficeOrdersFilter must be used within BackOfficeOrdersFilterProvider',
      );
    }
    return ctx;
  };

type ProviderProps = {
  children: React.ReactNode;
  initialStartISO?: string;
  initialEndISO?: string;
};

export const BackOfficeOrdersFilterProvider: React.FC<ProviderProps> = ({
  children,
  initialStartISO,
  initialEndISO,
}) => {
  const [startISO, setStartISO] = useState(
    initialStartISO || moment().startOf('month').toISOString(),
  );
  const [endISO, setEndISO] = useState(
    initialEndISO || moment().endOf('day').toISOString(),
  );
  const [visible, setVisible] = useState(false);
  const [dateFilterActive, setDateFilterActive] = useState(true);

  const setRange = useCallback((s: string, e: string) => {
    setStartISO(s);
    setEndISO(e);
    setDateFilterActive(true);
  }, []);

  const value = useMemo(
    () => ({
      startISO,
      endISO,
      dateFilterActive,
      setDateFilterActive,
      setRange,
      visible,
      setVisible,
    }),
    [startISO, endISO, dateFilterActive, setRange, visible],
  );

  return (
    <BackOfficeOrdersFilterContext.Provider value={value}>
      {children}
    </BackOfficeOrdersFilterContext.Provider>
  );
};

export default BackOfficeOrdersFilterContext;
